import Seo from "@/components/Seo";
import CTASection from "@/components/CTASection";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { CASE_STUDIES } from "@/data/content";

const Block = ({ label, children }) => (
  <div>
    <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-volt">{label}</p>
    <div className="mt-4">{children}</div>
  </div>
);

export default function CaseStudies() {
  return (
    <>
      <Seo
        title="QA Case Studies — Sample Testing Engagements | Avirova Technologies"
        description="Sample QA case studies showing how structured testing improves product quality: SaaS CRM functional and API testing, e-commerce checkout validation, and mobile compatibility testing."
      />

      <section className="relative overflow-hidden pt-36 pb-20 lg:pt-44 lg:pb-24" data-testid="case-studies-hero">
        <div className="grid-lines absolute inset-0" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <MaskedLine delay={0.1}>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-volt">
              {"// Case Studies"}
            </p>
          </MaskedLine>
          <h1 className="mt-6 max-w-4xl font-display text-5xl font-black leading-[0.98] tracking-tighter text-zinc-100 sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.2}>Structured QA,</MaskedLine>
            <MaskedLine delay={0.32}>
              measured in <span className="text-volt">outcomes.</span>
            </MaskedLine>
          </h1>
          <MaskedLine delay={0.46}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg">
              The case studies below are clearly labelled sample engagements
              that demonstrate our testing approach. Verified client results
              will replace them as they become available.
            </p>
          </MaskedLine>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-8 sm:px-8" data-testid="case-studies-list">
        {CASE_STUDIES.map((cs, i) => (
          <article
            key={cs.id}
            data-testid={`case-study-detail-${cs.id}`}
            className="border-t border-zinc-800/80 py-16 lg:py-20"
          >
            <div className="grid gap-10 lg:grid-cols-12">
              <div className="lg:col-span-4">
                <Reveal>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-volt">{cs.id} //</span>
                    <span className="rounded-full border border-volt/25 bg-volt/[0.06] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-volt">
                      {cs.label}
                    </span>
                  </div>
                  <h2 className="mt-5 font-display text-3xl font-extrabold tracking-tighter text-zinc-100 sm:text-4xl">
                    {cs.title}
                  </h2>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {cs.scope.map((s) => (
                      <span
                        key={s}
                        className="rounded-md border border-zinc-800 px-2.5 py-1 font-mono text-[10px] text-zinc-500"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                </Reveal>
              </div>
              <div className="grid gap-10 sm:grid-cols-2 lg:col-span-8">
                <Reveal delay={0.08}>
                  <Block label="Challenge">
                    <p className="text-sm leading-relaxed text-zinc-400">{cs.challenge}</p>
                  </Block>
                </Reveal>
                <Reveal delay={0.12}>
                  <Block label="Testing Approach">
                    <p className="text-sm leading-relaxed text-zinc-400">{cs.approach}</p>
                  </Block>
                </Reveal>
                <Reveal delay={0.16}>
                  <Block label="Coverage">
                    <ul className="space-y-2">
                      {cs.coverage.map((c) => (
                        <li key={c} className="flex gap-2.5 text-sm leading-relaxed text-zinc-400">
                          <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-volt" />
                          {c}
                        </li>
                      ))}
                    </ul>
                  </Block>
                </Reveal>
                <Reveal delay={0.2}>
                  <Block label="Findings">
                    <p className="text-sm leading-relaxed text-zinc-400">{cs.findings}</p>
                  </Block>
                </Reveal>
                <Reveal delay={0.24} className="sm:col-span-2">
                  <Block label="Outcome">
                    <p className="text-sm leading-relaxed text-zinc-400">{cs.outcome}</p>
                  </Block>
                </Reveal>
              </div>
            </div>
          </article>
        ))}
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        <Reveal>
          <p className="rounded-2xl border border-zinc-800 bg-ink-900/60 p-6 font-mono text-xs leading-relaxed tracking-wide text-zinc-500" data-testid="case-study-disclaimer">
            These are illustrative sample projects created to demonstrate our
            process and reporting style. They do not represent real clients, and
            no numerical results are claimed. Verified engagements will be
            published here with client permission.
          </p>
        </Reveal>
      </section>

      <CTASection />
    </>
  );
}
